import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export type LoaderPhase =
  | 'creating'
  | 'loading-builder'
  | 'generating-content'
  | 'finalizing'
  | 'activating-plan'

export const usePageLoaderStore = defineStore('pageLoader', () => {
  const active = ref(false)
  const phase = ref<LoaderPhase>('creating')
  const isFirstLoad = ref(false)

  // Sub-message index for the generating-content phase rotation
  const generatingSubIndex = ref(0)
  let subTimer: ReturnType<typeof setInterval> | null = null

  function startCreating(firstLoad = false) {
    active.value = true
    phase.value = 'creating'
    isFirstLoad.value = firstLoad
    generatingSubIndex.value = 0
  }

  function setPhase(newPhase: LoaderPhase) {
    phase.value = newPhase
    if (newPhase === 'generating-content') {
      startSubMessageRotation()
    } else {
      stopSubMessageRotation()
    }
  }

  function finish() {
    active.value = false
    stopSubMessageRotation()
  }

  function startSubMessageRotation() {
    stopSubMessageRotation()
    generatingSubIndex.value = 0
    subTimer = setInterval(() => {
      generatingSubIndex.value = Math.min(generatingSubIndex.value + 1, 4)
    }, 2400)
  }

  function stopSubMessageRotation() {
    if (subTimer) {
      clearInterval(subTimer)
      subTimer = null
    }
  }

  // i18n keys for the current phase
  const messageKey = computed(() => {
    switch (phase.value) {
      case 'creating': return 'pageLoader.creating'
      case 'loading-builder': return 'pageLoader.loadingBuilder'
      case 'generating-content': return 'pageLoader.generatingContent'
      case 'finalizing': return 'pageLoader.finalizing'
      case 'activating-plan': return 'pageLoader.activatingPlan'
    }
  })

  const subMessageKey = computed(() => {
    switch (phase.value) {
      case 'creating': return 'pageLoader.creatingSubtext'
      case 'loading-builder': return null
      case 'generating-content': return `pageLoader.generatingSubtext${generatingSubIndex.value + 1}`
      case 'finalizing': return null
      case 'activating-plan': return 'pageLoader.activatingPlanSubtext'
    }
  })

  return {
    active,
    phase,
    isFirstLoad,
    messageKey,
    subMessageKey,
    startCreating,
    setPhase,
    finish
  }
})
